import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Document } from "@models/document";
import { DocumentableType } from "@models/enums/documentable-type.enum";
import { from, map, Observable, switchMap } from "rxjs";
import { environment } from "../../environments/environment";
import { BlobStorageService } from "./blob-storage.service";

@Injectable({
  providedIn: "root",
})
export class DocumentsService {
  private readonly _http = inject(HttpClient);
  private readonly _blobService = inject(BlobStorageService);
  private readonly baseUrl = `${environment.API_BACKEND}/documents`;
  private readonly _sasToken = environment.SAS_TOKEN_BLOB_STORAGE_AZURE;

  getAll(
    documentableType: DocumentableType,
    documentableId: number,
  ): Observable<Document[]> {
    const params = new HttpParams()
      .set("documentable_type", documentableType)
      .set("documentable_id", documentableId);

    return this._http.get<Document[]>(this.baseUrl, { params });
  }

  upload(
    documentableType: DocumentableType,
    documentableId: number,
    file: File,
    title?: string,
  ): Observable<Document> {
    const blobPath = `documents/${documentableType}-${documentableId}/${file.name}`;
    const url = `${environment.BLOB_URL}/${blobPath}`;

    const params = new HttpParams()
      .set("documentable_type", documentableType)
      .set("documentable_id", documentableId);

    return from(
      this._blobService.uploadImageAsync(this._sasToken, file, blobPath),
    ).pipe(
      switchMap(() =>
        this._http.post<Document>(
          this.baseUrl,
          { document: { url, title: title ?? file.name } },
          { params },
        ),
      ),
    );
  }

  uploadInvoice(file: File): Observable<string> {
    const safeName = file.name.replace(/[^\w.\-]+/g, "_");
    const blobPath = `documents/invoices/${Date.now()}-${crypto.randomUUID()}-${safeName}`;
    const url = `${environment.BLOB_URL}/${blobPath}`;

    return from(
      this._blobService.uploadImageAsync(this._sasToken, file, blobPath),
    ).pipe(map(() => url));
  }

  delete(
    id: number,
    documentableType: DocumentableType,
    documentableId: number,
  ): Observable<void> {
    const params = new HttpParams()
      .set("documentable_type", documentableType)
      .set("documentable_id", documentableId);

    return this._http.delete<void>(`${this.baseUrl}/${id}`, { params });
  }
}
